import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import Slider from "@mui/material/Slider";
import { Button } from "@library/UI/Button";
import Input from "@components/library/UI/Input";
import { BaseDropdown } from "@components/library/UI/BaseDropdown";
import { DepositModal, TransferModal } from "./components";

import "./CopyTraderModal.sass";

import defaultAvatar from "@assets/images/default-avatar.svg";
import { ReactComponent as VerifiedIcon } from "@assets/images/icons/verified-check.svg";
import { ReactComponent as MedalIcon } from "@assets/images/icons/medal-gold.svg";
import { ReactComponent as ArrowUpGreen } from "@assets/images/icons/arrow-up-green.svg";
import { ReactComponent as CloseIcon } from "@assets/images/icons/close-dropdown.svg";
import { ReactComponent as DepositIcon } from "@assets/images/icons/money-bill-transfer.svg";
import { ReactComponent as TransferIcon } from "@assets/images/icons/money-from-bracket.svg";
import { ReactComponent as ChevronDown } from "@assets/images/icons/arrow-down-icon.svg";
import { ReactComponent as LockIcon } from "@assets/images/icons/lock-icon.svg";
import { ReactComponent as InfoIcon } from "@assets/images/icons/info-circle.svg";

export const CopyTraderModal = ({
  active,
  setActive,
  traderName = "High ROI",
}) => {
  const [trailingStop, setTrailingStop] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const [showDeposit, setShowDeposit] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);

  const [showMoreSettings, setShowMoreSettings] = useState(false);
  const [slRatio, setSlRatio] = useState(0); // Add this
  const [tpRatio, setTpRatio] = useState(0); // Add this

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      funding_account: "Funding Account",
    },
  });

  useEffect(() => {
    if (active) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [active]);

  const selectedAccount = watch("funding_account");

  const onSubmit = (data) => {
    console.log("Form Submitted:", data);
  };

  if (!active) return null;

  const ratioMarks = [0, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200].map(
    (val) => ({
      value: val,
      label: `${val}%`,
    }),
  );

  return (
    <div className="copy-modal-overlay" onClick={() => setActive(false)}>
      <div className="copy-modal" onClick={(e) => e.stopPropagation()}>
        {/* --- LEFT PANE --- */}
        <div className="copy-modal__left">
          <div className="copy-modal__left-bg"></div>

          <div className="copy-modal__avatar-wrap">
            <img
              src={defaultAvatar}
              alt="Avatar"
              className="copy-modal__avatar"
            />
          </div>

          <h2 className="copy-modal__trader-name">{traderName}</h2>

          <div className="copy-modal__badges">
            <span className="copy-modal__badge copy-modal__badge--verified">
              <VerifiedIcon /> Verified
            </span>

            <span className="copy-modal__badge copy-modal__badge--gold">
              <MedalIcon /> Top Performer
            </span>

            <span className="copy-modal__badge copy-modal__badge--green">
              <ArrowUpGreen /> Long-bias
            </span>
          </div>
          <div className="copy-modal__stats-container">
            <div className="copy-modal__stats-row">
              <div className="copy-modal__stat">
                <span className="copy-modal__stat-label">Days Trading</span>

                <span className="copy-modal__stat-value">312</span>
              </div>
              <div className="copy-modal__stat-divider"></div>

              <div className="copy-modal__stat">
                <span className="copy-modal__stat-label">Copiers</span>

                <span className="copy-modal__stat-value">142/300</span>
              </div>
              <div className="copy-modal__stat-divider"></div>

              <div className="copy-modal__stat">
                <span className="copy-modal__stat-label">Total Copiers</span>

                <span className="copy-modal__stat-value">1,895</span>
              </div>
            </div>

            <div className="copy-modal__stats-row">
              <div className="copy-modal__stat">
                <span className="copy-modal__stat-label">Mock Copiers</span>

                <span className="copy-modal__stat-value">1,183</span>
              </div>

              <div className="copy-modal__stat-divider"></div>

              <div className="copy-modal__stat">
                <span className="copy-modal__stat-label">
                  Closed Portfolios
                </span>
                <span className="copy-modal__stat-value">2</span>
              </div>
            </div>
          </div>
        </div>

        {/* --- RIGHT PANE --- */}
        <div className="copy-modal__right mainPopup">
          <div className="copy-modal__right-head">
            <div className="copy-modal__title-group">
              <h3 className="copy-modal__title">Copy {traderName}</h3>

              <span className="copy-modal__status-text">Now Enabled</span>
            </div>

            <button
              className="copy-modal__close-btn"
              onClick={() => setActive(false)}
            >
              <CloseIcon />
            </button>
          </div>

          <form className="mainPopupForm" onSubmit={handleSubmit(onSubmit)}>
            {/* INVESTMENT INPUT */}
            <div className="copy-modal__item">
              <Input
                className="copy-modal__amount-input"
                data={{
                  label: "Investment",
                  type: "number",
                  placeholder: "Min. 100",
                  errors,
                  settings: {
                    ...register("amount", { required: true, min: 100 }),
                  },
                  message: "Minimum investment is 100 USDT",
                  name: "amount",
                }}
              >
                <span className="copy-modal__input-suffix">USDT</span>
              </Input>
            </div>

            {/* ACCOUNT DROPDOWN */}
            <div className="copy-modal__item">
              <BaseDropdown
                className="copy-modal__account-dropdown"
                trigger={
                  <div className="mainPopupForm__amount-input-wrap copy-modal__dropdown-trigger">
                    <input
                      type="text"
                      className="mainPopupForm__amount-input copy-modal__input--readonly"
                      value={selectedAccount}
                      readOnly
                    />
                    <span className="mainPopupForm__amount-currency">
                      <ChevronDown />
                    </span>
                  </div>
                }
              >
                {({ closeDropdown }) => (
                  <div className="copy-modal__dropdown-menu">
                    {["Funding Account", "Spot Account", "Futures Account"].map(
                      (accountName) => (
                        <div
                          key={accountName}
                          className={`copy-modal__dropdown-item ${
                            selectedAccount === accountName
                              ? "copy-modal__dropdown-item--active"
                              : ""
                          }`}
                          onClick={() => {
                            setValue("funding_account", accountName, {
                              shouldValidate: true,
                            });
                            closeDropdown();
                          }}
                        >
                          {accountName}
                        </div>
                      ),
                    )}
                  </div>
                )}
              </BaseDropdown>
            </div>

            {/* TRAILING STOP & ACTION BUTTONS */}
            <div className="copy-modal__item">
              <div className="copy-modal__checkbox-wrap">
                <input
                  type="checkbox"
                  id="cp"
                  className="bo-checkbox2 copy-modal__checkbox"
                  checked={trailingStop}
                  onChange={() => setTrailingStop(!trailingStop)}
                  value={trailingStop}
                />
                <label
                  htmlFor="cp"
                  className="bo-checkbox2-label copy-modal__checkbox-label"
                >
                  <p className="copy-modal__checkbox-text">Trailing Stop</p>
                </label>
              </div>

              <div className="copy-modal__action-btns-wrap">
                <Button
                  className="copy-modal__action-btn"
                  type="button"
                  onClick={() => setShowDeposit(true)}
                >
                  <DepositIcon /> Deposit
                </Button>

                <Button
                  className="copy-modal__action-btn"
                  type="button"
                  onClick={() => setShowTransfer(true)}
                >
                  <TransferIcon /> Transfer
                </Button>
              </div>
            </div>

            {/* 2. CONDITIONAL ADVANCED SETTINGS */}
            {showMoreSettings && (
              <div className="copy-modal__advanced-settings">
                {/* Smart Copy Mode Dropdown */}
                <div className="copy-modal__item">
                  <BaseDropdown
                    hasSearch={false}
                    className="copy-modal__adv-dropdown"
                    trigger={
                      <div className="copy-modal__adv-dropdown-trigger">
                        <span className="copy-modal__adv-label-inner">
                          <LockIcon /> Smart Copy Mode
                        </span>
                        <ChevronDown className="copy-modal__adv-chevron" />
                      </div>
                    }
                  >
                    {({ closeDropdown }) => (
                      <div className="copy-modal__adv-menu">
                        {["Smart Copy Mode", "Advanced Copy Mode"].map(
                          (mode) => (
                            <div
                              key={mode}
                              className="copy-modal__adv-menu-item"
                              onClick={() => {
                                // setValue("copyMode", mode); // Uncomment when ready!
                                closeDropdown();
                              }}
                            >
                              {mode}
                            </div>
                          ),
                        )}
                      </div>
                    )}
                  </BaseDropdown>
                </div>

                {/* Using your existing Input for Margin Mode */}
                <div className="copy-modal__item">
                  <Input
                    className="copy-modal__amount-input" // Reusing this for the 60px right padding!
                    data={{
                      label: (
                        <span className="copy-modal__adv-label-inner">
                          <LockIcon /> Margin Mode{" "}
                          <InfoIcon className="copy-modal__info-icon" />
                        </span>
                      ),
                      type: "text",
                      placeholder: "Follow Master Trader's Setting",
                      name: "marginMode",
                      settings: { readOnly: true }, // Prevents typing in this field
                    }}
                  >
                    <span className="copy-modal__input-suffix">USDT</span>
                  </Input>
                </div>

                {/* Using your existing Input for Max Position */}
                <div className="copy-modal__item">
                  <Input
                    className="copy-modal__amount-input"
                    data={{
                      label: (
                        <span className="copy-modal__adv-label-inner">
                          <LockIcon /> Max. Position Margin Per Contract{" "}
                          <InfoIcon className="copy-modal__info-icon" />
                        </span>
                      ),
                      type: "number",
                      placeholder: "Please enter amount between 0 - 300000",
                      name: "maxPosition",
                      settings: { ...register("maxPosition") },
                    }}
                  >
                    <span className="copy-modal__input-suffix">USDT</span>
                  </Input>
                </div>

                {/* 1. Max. Daily Position Limit Input */}
                <div className="copy-modal__item">
                  <Input
                    className="copy-modal__amount-input"
                    data={{
                      label: (
                        <span className="copy-modal__adv-label-inner">
                          <LockIcon /> Max. Daily Position Limit{" "}
                          <InfoIcon className="copy-modal__info-icon" />
                        </span>
                      ),
                      type: "number",
                      placeholder: "Please enter amount between 0 - 300000",
                      name: "maxDaily",
                      settings: { ...register("maxDaily") },
                    }}
                  >
                    <span className="copy-modal__input-suffix">USDT</span>
                  </Input>
                </div>

                {/* 2. Stop-Loss Ratio Slider */}
                <div className="copy-modal__slider-wrap">
                  <span className="copy-modal__adv-label-inner">
                    <LockIcon /> Stop-Loss Ratio Per Order {slRatio}%
                  </span>

                  <Slider
                    className="custom-range copy-modal__slider"
                    value={slRatio}
                    onChange={(e, val) => setSlRatio(val)}
                    marks={ratioMarks}
                    step={20}
                    min={0}
                    max={200}
                  />
                </div>

                {/* 3. Take-Profit Ratio Slider */}
                <div className="copy-modal__slider-wrap">
                  <span className="copy-modal__adv-label-inner">
                    <LockIcon /> Take-Profit Ratio Per Order {tpRatio}%
                  </span>

                  <Slider
                    className="custom-range copy-modal__slider"
                    value={tpRatio}
                    onChange={(e, val) => setTpRatio(val)}
                    marks={ratioMarks}
                    step={20}
                    min={0}
                    max={200}
                  />
                </div>

                {/* 4. Perp Copy Stop Loss (PCSL) Dropdown */}
                <div className="copy-modal__item">
                  <BaseDropdown
                    className="copy-modal__adv-dropdown"
                    trigger={
                      <div style={{ cursor: "pointer" }}>
                        <Input
                          className="copy-modal__amount-input"
                          data={{
                            label: (
                              <span className="copy-modal__adv-label-inner">
                                <LockIcon /> Perp Copy Stop Loss (PCSL){" "}
                                <InfoIcon className="copy-modal__info-icon" />
                              </span>
                            ),
                            type: "text",
                            placeholder: "Please enter a value",
                            name: "pcsl",
                            settings: {
                              readOnly: true,
                              style: { cursor: "pointer" }, // Makes the input look clickable
                            },
                          }}
                        >
                          {/* We group USDT and the Chevron inside the existing suffix class */}
                          <div
                            className="copy-modal__input-suffix"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <span>USDT</span>
                            <ChevronDown
                              style={{ width: "16px", height: "16px" }}
                            />
                          </div>
                        </Input>
                      </div>
                    }
                  >
                    {({ closeDropdown }) => (
                      <div className="copy-modal__adv-menu">
                        {["Option 1", "Option 2"].map((opt) => (
                          <div
                            key={opt}
                            className="copy-modal__adv-menu-item"
                            onClick={() => {
                              closeDropdown();
                            }}
                          >
                            {opt}
                          </div>
                        ))}
                      </div>
                    )}
                  </BaseDropdown>
                </div>

                {/* 5. Perp CopyGuard Toggle */}
                <div className="copy-modal__adv-row">
                  <span className="copy-modal__adv-label-inner">
                    <LockIcon /> Perp CopyGuard
                  </span>

                  <label className="copy-modal__toggle">
                    <input type="checkbox" />
                    <span className="copy-modal__toggle-slider"></span>
                  </label>
                </div>

                {/* 6. Choose a Contract */}
                <div className="copy-modal__adv-row">
                  <span className="copy-modal__adv-label-inner">
                    <LockIcon /> Choose a contract
                  </span>

                  <span className="copy-modal__adv-value">
                    Total 340 contracts
                  </span>
                </div>
              </div>
            )}

            {/* 3. TOGGLE BUTTON */}
            <div
              className="copy-modal__more-settings"
              onClick={() => setShowMoreSettings(!showMoreSettings)}
            >
              {showMoreSettings ? "Hide Settings" : "More Settings"}
              <ChevronDown
                className={showMoreSettings ? "copy-modal__chevron-up" : ""}
              />
            </div>

            {/* FOOTER ACTIONS */}
            <div className="copy-modal__item copy-modal__footer">
              <div className="copy-modal__checkbox-wrap copy-modal__checkbox-wrap--small">
                <input
                  type="checkbox"
                  id="cp2"
                  className="bo-checkbox2 copy-modal__checkbox"
                  checked={agreed}
                  onChange={() => setAgreed(!agreed)}
                  value={agreed}
                />
                <label
                  htmlFor="cp2"
                  className="bo-checkbox2-label copy-modal__checkbox-label"
                >
                  <p className="copy-modal__checkbox-text">
                    I have read and agreed to
                    <a href="#"> Binify Copy Trading Agreement</a>
                  </p>
                </label>
              </div>

              <Button type="submit" className="copy-modal__submit-btn">
                Copy
              </Button>
            </div>
          </form>
        </div>
      </div>

      <DepositModal active={showDeposit} setActive={setShowDeposit} />
      <TransferModal active={showTransfer} setActive={setShowTransfer} />
    </div>
  );
};
